import { Component, HostBinding, Inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { WINDOW } from '@core/tools/window';
import { IDENTIFIER } from '@core/tools/regexp';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';

export interface BreadcrumbItem {
  label: string;
  route: string[];
}

@Component({
    selector: 'breadcrumb',
    templateUrl: './breadcrumb.component.html',
    styleUrls: ['./breadcrumb.component.scss'],
    providers: [{ provide: TRANSLOCO_SCOPE, useValue: { scope: 'ui-components/breadcrumb', alias: 'bc' } }],
    standalone: false
})

export class BreadcrumbComponent implements OnInit, OnDestroy {
  private _subject: Subject<void> = new Subject();
  public fragments: string[];
  public links: BreadcrumbItem[];

  @HostBinding() class = 'breadcrumb';

  constructor(
    private router: Router,
    @Inject(WINDOW) private readonly window: Window
  ) { }

  ngOnInit(): void {
    this.links = this.getLinks();
    this.routerWatch().subscribe();
  }

  ngOnDestroy(): void {
    this._subject.next();
    this._subject.complete();
  }

  getLocationFragments(): string[] {
    const url = new URL(this.window.location.href);
    const path = url.pathname.startsWith('/') ? url.pathname.substring(1) : url.pathname;
    return path.split('/');
  }

  getLinks(): BreadcrumbItem[] {
    const fragments = this.getLocationFragments();
    return fragments.reduce((acc: BreadcrumbItem[], curr: string, $i: number) => {
      return curr && curr !== '' && !IDENTIFIER.test(curr) ? [...acc, {label: this.getLabel(curr, $i), route: [...fragments.slice(0, $i + 1)]}] : acc;
    }, [])
  }

  getLabel(item: string, index: number): string {
    return ['new', 'edit', 'view'].includes(item) ? `${item}-${this.getLocationFragments()[index - 1]}` : item;
  }

  routerWatch(): Observable<NavigationEnd> {
    return this.router.events.pipe(
      takeUntil(this._subject),
      filter((event: unknown): event is NavigationEnd => event instanceof NavigationEnd),
      tap(() => this.links = this.getLinks()),
    )
  }
}
